import { useState, useEffect, useRef } from 'react';
import { db } from '../services/firebase';
import { doc, onSnapshot, runTransaction } from 'firebase/firestore';
import { mesclarQuadros, igual } from '../services/kanbanMesclar';
import { useLeads } from '../context/LeadsContext';
import { useNavigate } from 'react-router-dom';
import { useProtectedAction } from '../hooks/useProtectedAction';
import { useAuth } from '../context/AuthContext';

const CORES = ['#6c47ff','#3b82f6','#f59e0b','#10b981','#ef4444','#8b5cf6','#06b6d4','#f97316'];

const genId = () => Date.now().toString(36) + Math.random().toString(36).slice(2, 6);

const colunasPadrao = () => ([
  { id: 'novo',     titulo: 'Novo Lead',       cor: '#6c47ff' },
  { id: 'contato',  titulo: 'Em Contato',       cor: '#3b82f6' },
  { id: 'proposta', titulo: 'Proposta Enviada', cor: '#f59e0b' },
  { id: 'fechado',  titulo: 'Fechado',          cor: '#10b981' },
  { id: 'perdido',  titulo: 'Perdido',          cor: '#ef4444' },
]);

const boardPadrao = () => ({
  id: 'board_' + genId(), nome: 'Funil de Leads',
  colunas: colunasPadrao(), cards: [],
});

const ORIGENS = {
  extrator: { label: '🗺️ Extrator', cor: '#6c47ff' },
  disparo:  { label: '📢 Disparo',  cor: '#f59e0b' },
  avec:     { label: '💜 Base Avec', cor: '#8b5cf6' },
  planilha: { label: '📄 Planilha', cor: '#0ea5e9' },
};

// ── Leitura de planilha de leads ──────────────────────────────────────────────
// Reconhece o cabeçalho pelo nome da coluna, em português ou inglês, para o
// franqueado não precisar montar a planilha num formato específico.
const semAcento = (v) => String(v == null ? '' : v).normalize('NFD').replace(/[\u0300-\u036f]/g, '');
const normCab = (v) => semAcento(v).toLowerCase().trim().replace(/\s+/g, '_');

const APELIDOS = {
  nome:       ['nome', 'nome_completo', 'cliente', 'empresa', 'razao_social', 'nome_fantasia', 'name', 'full_name', 'estabelecimento', 'salao'],
  telefone:   ['telefone', 'celular', 'whatsapp', 'whats', 'fone', 'tel', 'contato', 'phone', 'mobile', 'numero', 'telefone_formatado'],
  email:      ['email', 'e_mail', 'e-mail', 'mail'],
  endereco:   ['endereco', 'endereço', 'address', 'logradouro', 'rua'],
  site:       ['site', 'website', 'url', 'instagram', 'perfil'],
  observacao: ['observacao', 'observacoes', 'obs', 'anotacao', 'anotacoes', 'notas', 'nota', 'comentario'],
};

// O disparador exige DDD + número. Aqui só normaliza: nunca inventa dígito.
const normalizarTelefone = (v) => {
  let d = String(v == null ? '' : v).replace(/\D/g, '');
  if (!d) return '';
  if (d.startsWith('00')) d = d.slice(2);
  if (d.length === 10 || d.length === 11) d = '55' + d;   // veio sem DDI
  return d;
};
const telefoneValido = (d) => d.length >= 12 && d.length <= 13 && d.startsWith('55');

async function lerPlanilhaLeads(file) {
  const XLSX = await import('xlsx');
  const buf = await file.arrayBuffer();
  const wb = XLSX.read(new Uint8Array(buf), { type: 'array', raw: false, codepage: 65001 });
  const ws = wb.Sheets[wb.SheetNames[0]];
  const linhas = XLSX.utils.sheet_to_json(ws, { header: 1, blankrows: false, defval: '' });
  if (!linhas.length) return { leads: [], colunasLidas: [] };

  // acha a linha de cabeçalho: a primeira que tenha alguma coluna reconhecível
  let iCab = 0;
  for (let i = 0; i < Math.min(linhas.length, 8); i++) {
    const c = linhas[i].map(normCab);
    if (c.some((x) => APELIDOS.nome.includes(x) || APELIDOS.telefone.includes(x))) { iCab = i; break; }
  }
  const cab = linhas[iCab].map(normCab);
  const acha = (chaves) => cab.findIndex((c) => chaves.includes(c));
  const idx = {
    nome: acha(APELIDOS.nome), telefone: acha(APELIDOS.telefone), email: acha(APELIDOS.email),
    endereco: acha(APELIDOS.endereco), site: acha(APELIDOS.site), observacao: acha(APELIDOS.observacao),
  };

  const pega = (l, i) => (i >= 0 && l[i] != null ? String(l[i]).trim() : '');
  const leads = [];
  for (const l of linhas.slice(iCab + 1)) {
    if (!l.some((c) => String(c).trim() !== '')) continue;
    const tel = normalizarTelefone(pega(l, idx.telefone));
    const nome = pega(l, idx.nome);
    if (!nome && !tel) continue;
    leads.push({
      nome: nome || 'Sem nome',
      telefone: tel,
      email: pega(l, idx.email),
      endereco: pega(l, idx.endereco),
      site: pega(l, idx.site),
      observacao: pega(l, idx.observacao),
    });
  }
  const colunasLidas = Object.entries(idx).filter(([, i]) => i >= 0).map(([k, i]) => k + ' ← ' + linhas[iCab][i]);
  return { leads, colunasLidas };
}

export default function Kanban() {
  const [boards, setBoards]   = useState([]);
  const [activeId, setActiveId] = useState(null);
  const [uid, setUid]         = useState(null);
  const [loading, setLoading] = useState(true);
  const [erro, setErro]       = useState('');

  const [drag, setDrag]         = useState(null);
  const [dragOver, setDragOver] = useState(null);
  const [novoNome, setNovoNome] = useState('');
  const [novoTel, setNovoTel]   = useState('');
  const [novoEmail, setNovoEmail] = useState('');
  const [novoCol, setNovoCol]   = useState('novo');
  const [modal, setModal]       = useState(false);
  const [editCol, setEditCol]   = useState(null);
  const [importModal, setImportModal] = useState(false); // seletor de quadro p/ leads recebidos
  const [cardAberto, setCardAberto]   = useState(null);  // detalhe do lead
  const [rascunho, setRascunho]       = useState('');    // nova anotação
  const [arquivoModal, setArquivoModal] = useState(false);
  const [previaArq, setPreviaArq]       = useState(null); // { leads, colunasLidas, colunaDestino }
  const [lendoArq, setLendoArq]         = useState(false);
  const [erroArq, setErroArq]           = useState('');

  const carregadoRef = useRef(false);
  // O quadro é da equipe toda. `baseRef` é a última versão do servidor que
  // esta tela conhece: é com ela que a gravação descobre o que ESTA pessoa
  // mudou e junta com o que os outros mudaram (services/kanbanMesclar.js).
  const baseRef = useRef(null);
  const boardsRef = useRef([]);
  useEffect(() => { boardsRef.current = boards; }, [boards]);
  // o quadro é da conta: o vendedor arrasta cards no funil do franqueado
  const { user, contaUid } = useAuth();
  const { paraKanban, setParaKanban, setParaDisparo } = useLeads();
  const navigate = useNavigate();
  const { protect, Modals, temPlano } = useProtectedAction(true);

  const lerQuadros = (snap) => {
    if (!snap.exists()) return [];
    try { return JSON.parse(snap.data().json || '{}').boards || []; } catch (e) { return []; }
  };

  // Escuta o quadro ao vivo: o que outra pessoa da equipe põe aparece
  // aqui sozinho, sem atualizar a página.
  useEffect(() => {
    if (!user || !contaUid) {
      setUid(null); setBoards([]); setLoading(false); setErro('Entre para usar o Kanban.');
      return undefined;
    }
    setUid(contaUid); setLoading(true); setErro('');
    carregadoRef.current = false; baseRef.current = null;
    const parar = onSnapshot(doc(db, 'users', contaUid, 'kanban', 'data'), (snap) => {
      const serv = lerQuadros(snap);
      if (!carregadoRef.current) {
        baseRef.current = serv;
        // Conta sem quadro: nasce o padrão (a gravação vê a diferença e salva).
        const lista = serv.length ? serv : [boardPadrao()];
        setBoards(lista);
        setActiveId(lista[0].id);
        setLoading(false);
        carregadoRef.current = true;
        return;
      }
      if (igual(serv, baseRef.current)) return;   // nada novo (ex.: a minha própria gravação)
      const base = baseRef.current;
      const local = boardsRef.current;
      baseRef.current = serv;
      setBoards(igual(local, base) ? serv : mesclarQuadros(base, local, serv));
    }, (e) => {
      setErro('Não consegui carregar seu Kanban: ' + e.message);
      setLoading(false);
    });
    return parar;
    // eslint-disable-next-line
  }, [user, contaUid]);

  // Grava juntando com o que está no servidor agora, nunca por cima.
  const salvar = async (local) => {
    const u = contaUid;
    if (!u) return;
    const ref = doc(db, 'users', u, 'kanban', 'data');
    try {
      let gravado = local;
      await runTransaction(db, async (t) => {
        const serv = lerQuadros(await t.get(ref));
        const base = baseRef.current || [];
        gravado = igual(serv, base) ? local : mesclarQuadros(base, local, serv);
        t.set(ref, { json: JSON.stringify({ boards: gravado }), atualizadoEm: new Date().toISOString() });
      });
      baseRef.current = gravado;
      // Chegou coisa dos outros na gravação: mostra, sem perder o que foi
      // mexido aqui enquanto gravava.
      if (!igual(gravado, local)) {
        setBoards((cur) => (igual(cur, local) ? gravado : mesclarQuadros(local, cur, gravado)));
      }
    } catch (e) {
      setErro('Falha ao salvar: ' + e.message);
    }
  };

  useEffect(() => {
    if (!carregadoRef.current || !uid) return undefined;
    if (igual(boards, baseRef.current)) return undefined;   // nada desta pessoa para gravar
    const t = setTimeout(() => salvar(boards), 500);
    return () => clearTimeout(t);
    // eslint-disable-next-line
  }, [boards]);

  // leads chegando pela ponte (Meus Leads / Extrator) -> abre seletor de quadro
  useEffect(() => {
    if (paraKanban && paraKanban.length && !loading && boards.length) {
      setImportModal(true);
    }
    // eslint-disable-next-line
  }, [paraKanban, loading, boards.length]);

  const criarCardsNoQuadro = (boardId) => {
    const alvo = boards.find((b) => b.id === boardId);
    if (!alvo) return;
    const primeiraCol = alvo.colunas[0]?.id;
    const novos = (paraKanban || []).map((l) => ({
      id: genId(),
      nome: l.nome,
      telefone: l.telefone_fmt || l.telefone || '',
      email: l.email || '',
      empresa: l.empresa || '',
      endereco: l.endereco || '',
      site: l.site || '',
      observacao: '',
      historico: [],
      coluna: primeiraCol,
      origem: 'extrator',
      criadoEm: new Date().toLocaleDateString('pt-BR'),
    }));
    setBoards((prev) => prev.map((b) => b.id === boardId ? { ...b, cards: [...b.cards, ...novos] } : b));
    setActiveId(boardId);
    setParaKanban(null);
    setImportModal(false);
  };

  const cancelarImport = () => { setParaKanban(null); setImportModal(false); };

  // ── Importar planilha de leads ──
  const escolherArquivo = async (file) => {
    if (!file) return;
    setLendoArq(true); setErroArq(''); setPreviaArq(null);
    try {
      const { leads, colunasLidas } = await lerPlanilhaLeads(file);
      if (!leads.length) throw new Error('Não encontrei leads na planilha. Ela precisa de uma coluna de nome ou de telefone no cabeçalho.');
      setPreviaArq({ leads, colunasLidas, arquivo: file.name, colunaDestino: board?.colunas?.[0]?.id || 'novo' });
    } catch (e) {
      setErroArq(e.message || String(e));
    } finally { setLendoArq(false); }
  };

  // Repetido é comparado pelo telefone: subir a mesma planilha duas vezes não
  // duplica card, e card duplicado viraria mensagem duplicada no disparo.
  const analisePrevia = () => {
    if (!previaArq || !board) return null;
    const jaNoQuadro = new Set(board.cards.map((c) => normalizarTelefone(c.telefone)).filter(Boolean));
    const vistos = new Set();
    const novos = [], repetidos = [], semNumero = [];
    for (const l of previaArq.leads) {
      if (!telefoneValido(l.telefone)) { semNumero.push(l); continue; }
      if (jaNoQuadro.has(l.telefone) || vistos.has(l.telefone)) { repetidos.push(l); continue; }
      vistos.add(l.telefone);
      novos.push(l);
    }
    return { novos, repetidos, semNumero };
  };

  const confirmarArquivo = (incluirSemNumero) => {
    const a = analisePrevia();
    if (!a || !board) return;
    const lista = incluirSemNumero ? [...a.novos, ...a.semNumero] : a.novos;
    if (!lista.length) return;
    const hoje = new Date().toLocaleDateString('pt-BR');
    const cards = lista.map((l) => ({
      id: genId(), nome: l.nome, telefone: l.telefone, email: l.email,
      empresa: '', endereco: l.endereco, site: l.site,
      observacao: l.observacao, historico: [],
      coluna: previaArq.colunaDestino, origem: 'planilha', criadoEm: hoje,
    }));
    updateBoard((b) => ({ ...b, cards: [...b.cards, ...cards] }));
    setPreviaArq(null); setArquivoModal(false); setErroArq('');
  };

  // board ativo
  const board = boards.find((b) => b.id === activeId) || boards[0];
  const updateBoard = (updater) =>
    setBoards((prev) => prev.map((b) => (b.id === (board && board.id) ? updater(b) : b)));

  // cards
  const addCard = () => {
    if (!novoNome.trim() || !board) return;
    const novo = {
      id: genId(), nome: novoNome, telefone: novoTel, email: novoEmail,
      empresa: '', endereco: '', site: '', observacao: '', historico: [],
      coluna: novoCol, origem: 'manual',
      criadoEm: new Date().toLocaleDateString('pt-BR'),
    };
    updateBoard((b) => ({ ...b, cards: [...b.cards, novo] }));
    setNovoNome(''); setNovoTel(''); setNovoEmail(''); setModal(false);
  };
  const moverCard = (cardId, novaColuna) =>
    updateBoard((b) => ({ ...b, cards: b.cards.map((c) => c.id === cardId ? { ...c, coluna: novaColuna } : c) }));
  // Edicao do lead: e o que faltava — clicar no card nao abria nada.
  const atualizarCard = (cardId, campos) =>
    updateBoard((b) => ({ ...b, cards: b.cards.map((c) => c.id === cardId ? { ...c, ...campos } : c) }));

  // Anotacao entra no historico com data, em vez de sobrescrever a anterior:
  // o Rodrigo quer registrar conversa, e conversa e acumulo.
  const adicionarNota = (cardId) => {
    const txt = rascunho.trim();
    if (!txt) return;
    const c = (board?.cards || []).find((x) => x.id === cardId);
    const nova = { texto: txt, em: new Date().toLocaleString('pt-BR', { dateStyle: 'short', timeStyle: 'short' }) };
    const historico = [nova, ...(Array.isArray(c?.historico) ? c.historico : [])];
    atualizarCard(cardId, { historico });
    setCardAberto((prev) => prev && prev.id === cardId ? { ...prev, historico } : prev);
    setRascunho('');
  };

  const deletarCard = (cardId) =>
    updateBoard((b) => ({ ...b, cards: b.cards.filter((c) => c.id !== cardId) }));

  // drag
  const onDragStart = (e, id) => setDrag(id);
  const onDragOver  = (e, id) => { e.preventDefault(); setDragOver(id); };
  const onDrop      = (e, colId) => {
    e.preventDefault();
    if (drag) moverCard(drag, colId);
    setDrag(null); setDragOver(null);
  };

  // manda os cards de uma coluna para a tela de Disparos
  const dispararColuna = (col, cards) => {
    const alvo = cards.filter((c) => String(c.telefone || '').replace(/\D/g, '').length >= 10);
    if (!alvo.length) return;
    setParaDisparo(alvo.map((c) => ({ telefone: c.telefone, nome: c.nome })));
    navigate('/disparo');
  };

  // colunas
  const addColuna = () =>
    updateBoard((b) => ({ ...b, colunas: [...b.colunas, {
      id: 'col_' + genId(), titulo: 'Nova coluna', cor: CORES[b.colunas.length % CORES.length],
    }] }));
  const salvarEditCol = () => {
    if (!editCol) return;
    updateBoard((b) => ({ ...b, colunas: b.colunas.map((c) => c.id === editCol.id ? editCol : c) }));
    setEditCol(null);
  };
  const deletarColuna = (colId) => {
    if (!window.confirm('Deletar coluna? Os cards dela serão perdidos.')) return;
    updateBoard((b) => ({
      ...b,
      colunas: b.colunas.filter((c) => c.id !== colId),
      cards:   b.cards.filter((c) => c.coluna !== colId),
    }));
  };

  // boards
  const addBoard = () => {
    const nome = window.prompt('Nome do novo quadro:', 'Novo quadro');
    if (!nome) return;
    const nb = { id: 'board_' + genId(), nome, colunas: colunasPadrao(), cards: [] };
    setBoards((prev) => [...prev, nb]);
    setActiveId(nb.id);
  };
  const renomearBoard = (b) => {
    const nome = window.prompt('Renomear quadro:', b.nome);
    if (!nome) return;
    setBoards((prev) => prev.map((x) => x.id === b.id ? { ...x, nome } : x));
  };
  const deletarBoard = (id) => {
    if (boards.length <= 1) { window.alert('Você precisa de pelo menos um quadro.'); return; }
    if (!window.confirm('Deletar este quadro inteiro? Todos os cards dele serão perdidos.')) return;
    setBoards((prev) => {
      const restantes = prev.filter((b) => b.id !== id);
      if (id === activeId) setActiveId(restantes[0].id);
      return restantes;
    });
  };

  // render
  if (loading) {
    return <div style={{ ...card, maxWidth: '400px', margin: '40px auto', textAlign: 'center',
      color: '#9898b8', fontSize: '13px' }}>Carregando seu Kanban...</div>;
  }
  if (erro && !board) {
    return <div style={{ ...card, maxWidth: '400px', margin: '40px auto', textAlign: 'center',
      color: '#dc2626', fontSize: '13px' }}>⚠ {erro}</div>;
  }
  if (!board) return null;

  return (
    <div>
      {Modals}
      <div style={{ display: 'flex', justifyContent: 'space-between',
        alignItems: 'flex-start', marginBottom: '18px' }}>
        <div>
          <h1 style={{ fontFamily: 'Syne, sans-serif', fontSize: '26px',
            fontWeight: '800', letterSpacing: '-0.5px', marginBottom: '6px' }}>
            Kanban de Leads
          </h1>
          <p style={{ color: '#52526e', fontSize: '13px' }}>
            Organize e acompanhe seus leads por etapa do funil.
          </p>
        </div>
        <div style={{ display: 'flex', gap: '8px' }}>
          <button onClick={addColuna} style={btnGhost}>+ Coluna</button>
          <button onClick={protect(() => { setErroArq(''); setPreviaArq(null); setArquivoModal(true); })}
            style={{ ...btnGhost, padding: '11px 18px', fontSize: '13px' }}>📄 Subir planilha</button>
          <button onClick={protect(() => { setNovoCol(board.colunas[0]?.id || 'novo'); setModal(true); })}
            style={btnPrimaryLg}>+ Lead</button>
        </div>
      </div>

      {/* Abas de quadros */}
      <div style={{ display: 'flex', alignItems: 'center', gap: '6px',
        marginBottom: '18px', flexWrap: 'wrap' }}>
        {boards.map((b) => {
          const ativo = b.id === board.id;
          return (
            <div key={b.id}
              onClick={() => setActiveId(b.id)}
              onDoubleClick={() => renomearBoard(b)}
              title="Clique para abrir · duplo-clique para renomear"
              style={{
                display: 'flex', alignItems: 'center', gap: '8px',
                padding: '7px 12px', borderRadius: '9px', cursor: 'pointer',
                fontSize: '13px', fontWeight: ativo ? '700' : '500',
                color: ativo ? '#6c47ff' : '#52526e',
                background: ativo ? 'rgba(108,71,255,0.08)' : '#fff',
                border: ativo ? '1.5px solid rgba(108,71,255,0.3)' : '1.5px solid rgba(0,0,0,0.08)',
              }}>
              {b.nome}
              {boards.length > 1 && (
                <span onClick={(e) => { e.stopPropagation(); deletarBoard(b.id); }}
                  style={{ color: '#c4c4d4', fontSize: '14px', lineHeight: 1 }}
                  onMouseEnter={(e) => e.target.style.color = '#ef4444'}
                  onMouseLeave={(e) => e.target.style.color = '#c4c4d4'}>×</span>
              )}
            </div>
          );
        })}
        <button onClick={addBoard} style={{ ...btnGhost, padding: '7px 12px' }}>+ Quadro</button>
      </div>

      {/* Board */}
      <div style={{ display: 'flex', gap: '12px', overflowX: 'auto', paddingBottom: '16px' }}>
        {board.colunas.map((col) => {
          const colCards = board.cards.filter((c) => c.coluna === col.id);
          const comTelefone = colCards.filter((c) => String(c.telefone || '').replace(/\D/g, '').length >= 10);
          return (
            <div key={col.id}
              onDragOver={(e) => onDragOver(e, col.id)}
              onDrop={(e) => onDrop(e, col.id)}
              style={{
                minWidth: '230px', flex: '0 0 230px', alignSelf: 'flex-start',
                background: dragOver === col.id ? 'rgba(108,71,255,0.05)' : '#f0f0f8',
                borderRadius: '12px', padding: '12px',
                border: dragOver === col.id ? '2px dashed #6c47ff' : '2px solid transparent',
              }}>
              <div style={{ display: 'flex', justifyContent: 'space-between',
                alignItems: 'center', marginBottom: '12px' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '7px', flex: 1 }}>
                  <div style={{ width: '8px', height: '8px', borderRadius: '50%',
                    background: col.cor, flexShrink: 0 }} />
                  <span onClick={() => setEditCol({ ...col })}
                    style={{ fontFamily: 'Syne, sans-serif', fontWeight: '700',
                      fontSize: '12px', color: '#0a0a18', cursor: 'pointer' }}>
                    {col.titulo}
                  </span>
                </div>
                <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                  <span style={{ fontFamily: 'monospace', fontSize: '11px',
                    background: 'rgba(0,0,0,0.08)', borderRadius: '6px',
                    padding: '1px 7px', color: '#52526e' }}>{colCards.length}</span>
                  <button onClick={() => deletarColuna(col.id)}
                    style={{ border: 'none', background: 'none', cursor: 'pointer',
                      color: '#c4c4d4', fontSize: '14px', padding: '0 2px', lineHeight: 1 }}
                    onMouseEnter={(e) => e.target.style.color = '#ef4444'}
                    onMouseLeave={(e) => e.target.style.color = '#c4c4d4'}>×</button>
                </div>
              </div>

              {comTelefone.length > 0 && (
                <button onClick={protect(() => dispararColuna(col, colCards))}
                  style={{ width: '100%', marginBottom: '10px', padding: '8px', borderRadius: '9px',
                    border: '1.5px dashed rgba(108,71,255,0.35)', background: 'rgba(108,71,255,0.04)',
                    color: '#6c47ff', fontSize: '12px', fontWeight: '600', cursor: 'pointer',
                    fontFamily: 'DM Sans, sans-serif' }}>
                  🚀 Disparar para {comTelefone.length}
                  {comTelefone.length < colCards.length && (
                    <span style={{ color: '#9898b8', fontWeight: '400' }}> · {colCards.length - comTelefone.length} sem número</span>
                  )}
                </button>
              )}

              <div style={{ maxHeight: 'calc(100vh - 300px)', overflowY: 'auto', margin: '0 -4px', padding: '0 4px' }}>
              {colCards.map((cardItem) => {
                const orig = ORIGENS[cardItem.origem];
                return (
                  <div key={cardItem.id} draggable
                    onDragStart={(e) => onDragStart(e, cardItem.id)}
                    onClick={() => { setCardAberto(cardItem); setRascunho(''); }}
                    title="Clique para abrir o lead"
                    style={{
                      background: '#fff', borderRadius: '10px', padding: '12px',
                      marginBottom: '8px', cursor: 'pointer',
                      boxShadow: drag === cardItem.id ? '0 8px 24px rgba(108,71,255,0.2)' : '0 1px 3px rgba(0,0,0,0.08)',
                      border: '1px solid rgba(0,0,0,0.06)',
                      opacity: drag === cardItem.id ? 0.6 : 1,
                    }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                      <div style={{ fontWeight: '600', fontSize: '13px', marginBottom: '4px', flex: 1 }}>
                        {cardItem.nome}
                      </div>
                      <button onClick={(e) => { e.stopPropagation(); deletarCard(cardItem.id); }}
                        style={{ border: 'none', background: 'none', cursor: 'pointer',
                          color: '#c4c4d4', fontSize: '14px', padding: '0 0 0 4px', lineHeight: 1 }}
                        onMouseEnter={(e) => e.target.style.color = '#ef4444'}
                        onMouseLeave={(e) => e.target.style.color = '#c4c4d4'}>×</button>
                    </div>
                    {cardItem.telefone && (
                      <div style={{ fontSize: '11px', color: '#52526e',
                        fontFamily: 'monospace', marginBottom: '8px' }}>
                        {'📱 ' + cardItem.telefone}
                      </div>
                    )}
                    {cardItem.email && (
                      <div style={{ fontSize: '11px', color: '#52526e', marginBottom: '8px',
                        overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                        {'✉️ ' + cardItem.email}
                      </div>
                    )}
                    <div style={{ display: 'flex', gap: '4px', flexWrap: 'wrap', marginBottom: '8px' }}>
                      {orig && (
                        <span style={{ fontSize: '10px', fontWeight: '600',
                          color: orig.cor, background: orig.cor + '14', borderRadius: '6px',
                          padding: '1px 7px' }}>{orig.label}</span>
                      )}
                      {(cardItem.historico?.length > 0 || cardItem.observacao) && (
                        <span title="Tem anotações"
                          style={{ fontSize: '10px', fontWeight: '600', color: '#52526e',
                            background: 'rgba(0,0,0,0.05)', borderRadius: '6px', padding: '1px 7px' }}>
                          📝 {cardItem.historico?.length || 1}
                        </span>
                      )}
                    </div>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                      <span style={{ fontSize: '10px', color: '#9898b8', fontFamily: 'monospace' }}>
                        {cardItem.criadoEm}
                      </span>
                      <select value={cardItem.coluna}
                        onChange={(e) => { e.stopPropagation(); moverCard(cardItem.id, e.target.value); }}
                        onClick={(e) => e.stopPropagation()}
                        style={{ fontSize: '10px', border: '1px solid rgba(0,0,0,0.1)',
                          borderRadius: '6px', padding: '2px 4px', background: '#f8f8fc',
                          cursor: 'pointer', color: '#52526e' }}>
                        {board.colunas.map((c) => <option key={c.id} value={c.id}>{c.titulo}</option>)}
                      </select>
                    </div>
                  </div>
                );
              })}

              {colCards.length === 0 && (
                <div style={{ textAlign: 'center', padding: '20px 0', color: '#c4c4d4', fontSize: '12px' }}>
                  Arraste leads aqui
                </div>
              )}
              </div>

            </div>
          );
        })}
      </div>

      {/* Modal: seletor de quadro p/ leads recebidos */}
      {importModal && paraKanban && (
        <div style={modalBg}>
          <div style={{ ...modalBox, width: '420px' }}>
            <div style={modalHead}>
              <h2 style={modalTitle}>Criar {paraKanban.length} card(s)</h2>
              <button onClick={cancelarImport} style={modalX}>×</button>
            </div>
            <p style={{ fontSize: '13px', color: '#52526e', marginBottom: '16px' }}>
              Em qual quadro você quer adicionar esses leads? Eles entram na primeira coluna.
            </p>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '8px', marginBottom: '8px' }}>
              {boards.map((b) => (
                <button key={b.id} onClick={() => criarCardsNoQuadro(b.id)}
                  style={{ textAlign: 'left', padding: '12px 16px', borderRadius: '10px',
                    border: '1.5px solid rgba(0,0,0,0.1)', background: '#f8f8fc', cursor: 'pointer',
                    fontSize: '13px', fontWeight: '600', color: '#0a0a18' }}
                  onMouseEnter={(e) => e.currentTarget.style.borderColor = '#6c47ff'}
                  onMouseLeave={(e) => e.currentTarget.style.borderColor = 'rgba(0,0,0,0.1)'}>
                  {b.nome} <span style={{ color: '#9898b8', fontWeight: '400' }}>· {b.cards.length} cards</span>
                </button>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* Modal novo lead */}
      {modal && (
        <div style={modalBg}>
          <div style={{ ...modalBox, width: '420px' }}>
            <div style={modalHead}>
              <h2 style={modalTitle}>Novo lead</h2>
              <button onClick={() => setModal(false)} style={modalX}>×</button>
            </div>
            <div style={{ marginBottom: '14px' }}>
              <label style={lbl}>Nome</label>
              <input value={novoNome} onChange={(e) => setNovoNome(e.target.value)}
                placeholder="Ex: Studio Bella Vita" style={inp}
                onKeyDown={(e) => e.key === 'Enter' && addCard()} />
            </div>
            <div style={{ marginBottom: '14px' }}>
              <label style={lbl}>Telefone</label>
              <input value={novoTel} onChange={(e) => setNovoTel(e.target.value)}
                placeholder="Ex: 5541999887766" style={inp} />
            </div>
            <div style={{ marginBottom: '14px' }}>
              <label style={lbl}>E-mail <span style={{ opacity: .6, textTransform: 'none' }}>(opcional)</span></label>
              <input value={novoEmail} onChange={(e) => setNovoEmail(e.target.value)}
                placeholder="contato@empresa.com.br" style={inp} />
            </div>
            <div style={{ marginBottom: '20px' }}>
              <label style={lbl}>Coluna</label>
              <select value={novoCol} onChange={(e) => setNovoCol(e.target.value)} style={inp}>
                {board.colunas.map((c) => <option key={c.id} value={c.id}>{c.titulo}</option>)}
              </select>
            </div>
            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '10px' }}>
              <button onClick={() => setModal(false)} style={btnGhost}>Cancelar</button>
              <button onClick={addCard} style={btnPrimary}>Adicionar</button>
            </div>
          </div>
        </div>
      )}

      {/* Modal: detalhe do lead — antes o card não abria nada */}
      {cardAberto && (() => {
        const c = (board?.cards || []).find((x) => x.id === cardAberto.id) || cardAberto;
        const orig = ORIGENS[c.origem];
        const tel = String(c.telefone || '').replace(/\D/g, '');
        const hist = Array.isArray(c.historico) ? c.historico : [];
        const campo = (label, chave, place, tipo) => (
          <div style={{ marginBottom: '12px' }}>
            <label style={lbl}>{label}</label>
            <input type={tipo || 'text'} value={c[chave] || ''} placeholder={place} style={inp}
              onChange={(e) => atualizarCard(c.id, { [chave]: e.target.value })} />
          </div>
        );
        return (
          <div style={modalBg} onClick={() => setCardAberto(null)}>
            <div style={{ ...modalBox, width: '560px', maxHeight: '88vh', overflowY: 'auto' }}
              onClick={(e) => e.stopPropagation()}>
              <div style={modalHead}>
                <div>
                  <h2 style={modalTitle}>{c.nome || 'Lead'}</h2>
                  <div style={{ fontSize: '11px', color: '#52526e', fontFamily: 'monospace', marginTop: '4px' }}>
                    {orig ? orig.label + ' · ' : ''}criado em {c.criadoEm || '—'}
                  </div>
                </div>
                <button onClick={() => setCardAberto(null)} style={modalX}>×</button>
              </div>

              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px' }}>
                {campo('Nome', 'nome', 'Ex: Studio Bella Vita')}
                {campo('Telefone', 'telefone', 'Ex: 5541999887766')}
                {campo('E-mail', 'email', 'contato@empresa.com.br', 'email')}
                {campo('Empresa / responsável', 'empresa', 'Ex: Rodrigo (sócio)')}
              </div>
              {campo('Endereço', 'endereco', 'Rua, número, bairro')}
              {campo('Site ou Instagram', 'site', 'https://')}

              <div style={{ marginBottom: '12px' }}>
                <label style={lbl}>Etapa do funil</label>
                <select value={c.coluna} style={inp}
                  onChange={(e) => { atualizarCard(c.id, { coluna: e.target.value }); }}>
                  {board.colunas.map((x) => <option key={x.id} value={x.id}>{x.titulo}</option>)}
                </select>
              </div>

              <div style={{ marginBottom: '16px' }}>
                <label style={lbl}>Anotações fixas <span style={{ opacity: .6, textTransform: 'none' }}>(o que sempre importa saber)</span></label>
                <textarea value={c.observacao || ''} rows={3}
                  placeholder="Ex: atende só de tarde, decisor é a esposa, já usou concorrente X"
                  onChange={(e) => atualizarCard(c.id, { observacao: e.target.value })}
                  style={{ ...inp, resize: 'vertical', fontFamily: 'DM Sans, sans-serif' }} />
              </div>

              <div style={{ borderTop: '1px solid rgba(0,0,0,0.07)', paddingTop: '16px' }}>
                <label style={lbl}>Registro de conversas</label>
                <div style={{ display: 'flex', gap: '8px', marginTop: '6px', marginBottom: '12px' }}>
                  <input value={rascunho} onChange={(e) => setRascunho(e.target.value)}
                    onKeyDown={(e) => e.key === 'Enter' && adicionarNota(c.id)}
                    placeholder="O que aconteceu nessa conversa?" style={{ ...inp, flex: 1 }} />
                  <button onClick={() => adicionarNota(c.id)} style={btnPrimary}>Registrar</button>
                </div>
                {hist.length === 0 ? (
                  <div style={{ fontSize: '12px', color: '#6b6b85' }}>
                    Nenhuma conversa registrada ainda. Cada registro fica com data e hora, um embaixo do outro.
                  </div>
                ) : (
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                    {hist.map((h, i) => (
                      <div key={i} style={{ background: '#f8f8fc', borderRadius: '9px', padding: '10px 12px' }}>
                        <div style={{ fontSize: '10px', color: '#6b6b85', fontFamily: 'monospace', marginBottom: '3px' }}>
                          {h.em}
                        </div>
                        <div style={{ fontSize: '13px', color: '#0a0a18', whiteSpace: 'pre-wrap' }}>{h.texto}</div>
                      </div>
                    ))}
                  </div>
                )}
              </div>

              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center',
                gap: '10px', marginTop: '20px', paddingTop: '16px', borderTop: '1px solid rgba(0,0,0,0.07)' }}>
                <button onClick={() => { if (window.confirm('Apagar este lead?')) { deletarCard(c.id); setCardAberto(null); } }}
                  style={{ ...btnGhost, color: '#dc2626', borderColor: 'rgba(220,38,38,0.3)' }}>Apagar lead</button>
                <div style={{ display: 'flex', gap: '10px' }}>
                  {tel.length >= 10 && (
                    <a href={`https://wa.me/${tel}`} target="_blank" rel="noreferrer"
                      style={{ ...btnGhost, textDecoration: 'none', display: 'inline-block' }}>Abrir WhatsApp</a>
                  )}
                  <button onClick={() => setCardAberto(null)} style={btnPrimary}>Pronto</button>
                </div>
              </div>
            </div>
          </div>
        );
      })()}

      {/* Modal: subir planilha de leads */}
      {arquivoModal && (
        <div style={modalBg}>
          <div style={{ ...modalBox, width: '560px', maxHeight: '88vh', overflowY: 'auto' }}>
            <div style={modalHead}>
              <h2 style={modalTitle}>Subir planilha de leads</h2>
              <button onClick={() => { setArquivoModal(false); setPreviaArq(null); setErroArq(''); }} style={modalX}>×</button>
            </div>

            {!previaArq && (
              <>
                <div onClick={() => document.getElementById('kb-arquivo').click()}
                  style={{ border: '2px dashed rgba(108,71,255,0.3)', borderRadius: '12px', padding: '28px',
                    textAlign: 'center', cursor: 'pointer', background: '#f8f8fc', marginBottom: '14px' }}>
                  <div style={{ fontSize: '28px', marginBottom: '8px' }}>{lendoArq ? '⟳' : '📄'}</div>
                  <div style={{ fontFamily: 'Syne, sans-serif', fontWeight: '700', fontSize: '14px', marginBottom: '4px' }}>
                    {lendoArq ? 'Lendo a planilha...' : 'Clique para selecionar'}
                  </div>
                  <div style={{ fontSize: '12px', color: '#6b6b85' }}>.xlsx, .xls ou .csv</div>
                  <input id="kb-arquivo" type="file" accept=".xlsx,.xls,.csv" hidden
                    onChange={(e) => { const f = e.target.files[0]; e.target.value = ''; escolherArquivo(f); }} />
                </div>
                <div style={{ fontSize: '12px', color: '#52526e', lineHeight: 1.7 }}>
                  A planilha precisa de uma linha de cabeçalho. Reconheço colunas chamadas
                  <strong> nome</strong>, <strong>telefone</strong> (ou celular, whatsapp),
                  <strong> email</strong>, <strong>endereço</strong>, <strong>site</strong> e
                  <strong> observação</strong> — em qualquer ordem. Telefone sem DDI recebe o 55 automaticamente.
                </div>
              </>
            )}

            {erroArq && (
              <div style={{ background: 'rgba(239,68,68,0.08)', border: '1px solid rgba(239,68,68,0.2)',
                borderRadius: '10px', padding: '12px 16px', marginTop: '14px', fontSize: '13px', color: '#dc2626' }}>
                ⚠ {erroArq}
              </div>
            )}

            {previaArq && (() => {
              const a = analisePrevia();
              if (!a) return null;
              return (
                <>
                  <div style={{ fontSize: '12px', color: '#52526e', fontFamily: 'monospace', marginBottom: '12px' }}>
                    {previaArq.arquivo} · {previaArq.leads.length} linhas
                  </div>

                  <div style={{ background: '#f8f8fc', borderRadius: '10px', padding: '12px 14px', marginBottom: '14px' }}>
                    <div style={{ ...lbl, marginBottom: '6px' }}>Colunas reconhecidas</div>
                    <div style={{ fontSize: '12px', color: '#52526e', fontFamily: 'monospace', lineHeight: 1.7 }}>
                      {previaArq.colunasLidas.map((c) => <div key={c}>{c}</div>)}
                    </div>
                  </div>

                  <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3,1fr)', gap: '8px', marginBottom: '14px' }}>
                    {[['Novos', a.novos.length, '#047857'],
                      ['Repetidos', a.repetidos.length, '#b45309'],
                      ['Sem número', a.semNumero.length, '#6b6b85']].map(([t, n, cor]) => (
                      <div key={t} style={{ border: '1px solid rgba(0,0,0,0.08)', borderRadius: '10px',
                        padding: '10px 12px', textAlign: 'center' }}>
                        <div style={{ fontFamily: 'Syne, sans-serif', fontWeight: '800', fontSize: '20px', color: cor }}>{n}</div>
                        <div style={{ fontSize: '10px', color: '#6b6b85', fontFamily: 'monospace', textTransform: 'uppercase' }}>{t}</div>
                      </div>
                    ))}
                  </div>

                  {a.repetidos.length > 0 && (
                    <div style={{ fontSize: '12px', color: '#b45309', marginBottom: '12px' }}>
                      {a.repetidos.length} já estão neste quadro (mesmo telefone) e ficam de fora, para o disparo não
                      mandar duas mensagens pra mesma pessoa.
                    </div>
                  )}

                  <div style={{ marginBottom: '14px' }}>
                    <label style={lbl}>Entrar na coluna</label>
                    <select value={previaArq.colunaDestino} style={inp}
                      onChange={(e) => setPreviaArq({ ...previaArq, colunaDestino: e.target.value })}>
                      {board.colunas.map((c) => <option key={c.id} value={c.id}>{c.titulo}</option>)}
                    </select>
                  </div>

                  <div style={{ maxHeight: '160px', overflowY: 'auto', border: '1px solid rgba(0,0,0,0.08)',
                    borderRadius: '10px', marginBottom: '16px' }}>
                    {a.novos.slice(0, 30).map((l, i) => (
                      <div key={i} style={{ display: 'flex', justifyContent: 'space-between', gap: '10px',
                        padding: '7px 12px', fontSize: '12px', borderTop: i ? '1px solid rgba(0,0,0,0.05)' : 'none' }}>
                        <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{l.nome}</span>
                        <span style={{ fontFamily: 'monospace', color: '#52526e' }}>{l.telefone}</span>
                      </div>
                    ))}
                    {a.novos.length > 30 && (
                      <div style={{ padding: '7px 12px', fontSize: '11px', color: '#6b6b85',
                        borderTop: '1px solid rgba(0,0,0,0.05)' }}>e mais {a.novos.length - 30}...</div>
                    )}
                  </div>

                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: '10px' }}>
                    <button onClick={() => { setPreviaArq(null); setErroArq(''); }} style={btnGhost}>Trocar arquivo</button>
                    <div style={{ display: 'flex', gap: '10px' }}>
                      {a.semNumero.length > 0 && (
                        <button onClick={() => confirmarArquivo(true)} style={btnGhost}>
                          Criar {a.novos.length + a.semNumero.length} (com os sem número)
                        </button>
                      )}
                      <button onClick={() => confirmarArquivo(false)} disabled={!a.novos.length}
                        style={a.novos.length ? btnPrimary : { ...btnPrimary, background: '#e5e7eb', color: '#6b6b85', cursor: 'not-allowed' }}>
                        Criar {a.novos.length} cards
                      </button>
                    </div>
                  </div>
                </>
              );
            })()}
          </div>
        </div>
      )}

      {/* Modal editar coluna */}
      {editCol && (
        <div style={modalBg}>
          <div style={{ ...modalBox, width: '380px' }}>
            <div style={modalHead}>
              <h2 style={modalTitle}>Editar coluna</h2>
              <button onClick={() => setEditCol(null)} style={modalX}>×</button>
            </div>
            <div style={{ marginBottom: '16px' }}>
              <label style={lbl}>Nome da coluna</label>
              <input value={editCol.titulo}
                onChange={(e) => setEditCol((prev) => ({ ...prev, titulo: e.target.value }))}
                style={inp} autoFocus />
            </div>
            <div style={{ marginBottom: '20px' }}>
              <label style={lbl}>Cor</label>
              <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
                {CORES.map((cor) => (
                  <button key={cor} onClick={() => setEditCol((prev) => ({ ...prev, cor }))}
                    style={{ width: '32px', height: '32px', borderRadius: '50%', background: cor,
                      border: editCol.cor === cor ? '3px solid #0a0a18' : '3px solid transparent',
                      cursor: 'pointer' }} />
                ))}
              </div>
            </div>
            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '10px' }}>
              <button onClick={() => setEditCol(null)} style={btnGhost}>Cancelar</button>
              <button onClick={salvarEditCol} style={btnPrimary}>Salvar</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

const card = { background: '#fff', border: '1px solid rgba(0,0,0,0.08)',
  borderRadius: '14px', padding: '24px', boxShadow: '0 1px 3px rgba(0,0,0,0.05)' };
const lbl = { fontSize: '11px', fontWeight: '700', color: '#52526e',
  textTransform: 'uppercase', letterSpacing: '0.06em',
  fontFamily: 'monospace', display: 'block', marginBottom: '6px' };
const inp = { width: '100%', padding: '10px 12px', borderRadius: '9px',
  border: '1.5px solid rgba(0,0,0,0.1)', fontSize: '14px',
  background: '#f8f8fc', outline: 'none', boxSizing: 'border-box',
  fontFamily: 'DM Sans, sans-serif', color: '#0a0a18' };
const btnGhost = { border: '1.5px solid rgba(0,0,0,0.1)', background: 'none',
  borderRadius: '8px', padding: '7px 14px', fontSize: '12px',
  cursor: 'pointer', color: '#52526e', fontFamily: 'DM Sans, sans-serif' };
const btnPrimary = { background: 'linear-gradient(135deg, #6c47ff, #5535e0)',
  color: '#fff', border: 'none', borderRadius: '10px', padding: '9px 22px',
  fontSize: '14px', fontWeight: '600', cursor: 'pointer' };
const btnPrimaryLg = { ...btnPrimary, padding: '11px 22px' };
const modalBg = { position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.4)',
  zIndex: 200, display: 'flex', alignItems: 'center', justifyContent: 'center' };
const modalBox = { background: '#fff', borderRadius: '16px', padding: '28px',
  boxShadow: '0 20px 60px rgba(0,0,0,0.2)' };
const modalHead = { display: 'flex', justifyContent: 'space-between', marginBottom: '20px' };
const modalTitle = { fontFamily: 'Syne, sans-serif', fontWeight: '800', fontSize: '18px' };
const modalX = { border: 'none', background: 'none', fontSize: '22px', cursor: 'pointer', color: '#9898b8' };
